import { COUNTRY_ORDER, getCountry } from "@orvis/config";
import type { CountryConfig } from "@orvis/core";

/**
 * Runtime config for one build.
 *
 * Country resolution order (first hit wins):
 *   1. localStorage["orvis:country"] — remembered from a previous session
 *      or from an in-app switch. Cleared when the user picks "Change".
 *   2. `?country=` URL query param — enables sharing a country-specific
 *      link. Written to localStorage when we honor it, so a refresh
 *      preserves the choice.
 *   3. `VITE_ORVIS_COUNTRY` env — baked into the bundle at build time.
 *      This is the ONLY signal the mobile builds carry, and it's how one
 *      binary per country still becomes the mobile shipping model.
 *
 * `canSwitch` decides whether the app renders the country picker UI:
 *   - Mobile builds set `VITE_ORVIS_COUNTRY` and DO NOT set
 *     `VITE_ORVIS_ALLOW_SWITCH`. → picker never shows, no switch button.
 *   - Web builds set `VITE_ORVIS_ALLOW_SWITCH=true` (and can also set a
 *     default `VITE_ORVIS_COUNTRY` as the first-run default). → picker
 *     shows on first run when nothing has been remembered; a "Change"
 *     control appears in the header.
 */

export const COUNTRY_STORAGE_KEY = "orvis:country";
const DEFAULT_FALLBACK_COUNTRY = "cr";

export interface FirebaseWebConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  /** GA4 measurement id (`G-XXXXXXXXXX`). Empty disables Analytics entirely. */
  measurementId: string;
}

export interface RuntimeConfig {
  country: CountryConfig;
  firebase: FirebaseWebConfig;
  /**
   * When true, the app renders the country picker on first run and a
   * "Change country" affordance in the header. Set by
   * `VITE_ORVIS_ALLOW_SWITCH=true` (web builds); mobile builds leave it
   * false so the picker never appears.
   */
  canSwitch: boolean;
}

function readLocalStorage(key: string): string | undefined {
  try {
    if (typeof localStorage === "undefined") return undefined;
    const v = localStorage.getItem(key);
    return v ?? undefined;
  } catch {
    // Safari in private mode + iframe sandbox both throw here.
    return undefined;
  }
}

function readQueryParam(name: string): string | undefined {
  try {
    if (typeof location === "undefined") return undefined;
    const v = new URLSearchParams(location.search).get(name);
    return v ?? undefined;
  } catch {
    return undefined;
  }
}

function writeLocalStorage(key: string, value: string): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function readFirebaseConfig(env: ImportMetaEnv): FirebaseWebConfig {
  return {
    apiKey: env.VITE_FIREBASE_API_KEY ?? "",
    authDomain: env.VITE_FIREBASE_AUTH_DOMAIN ?? "",
    projectId: env.VITE_FIREBASE_PROJECT_ID ?? "orvisapp",
    storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET ?? "",
    messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? "",
    appId: env.VITE_FIREBASE_APP_ID ?? "",
    measurementId: env.VITE_FIREBASE_MEASUREMENT_ID ?? "",
  };
}

/**
 * Pick the country code for this session. Returns `undefined` when no
 * hint has been given AND the build is in switch-allowed mode — the
 * caller renders the picker in that case.
 */
export function resolveCountryCode(env: ImportMetaEnv = import.meta.env): string | undefined {
  const canSwitch = env.VITE_ORVIS_ALLOW_SWITCH === "true";

  const candidates = [
    readLocalStorage(COUNTRY_STORAGE_KEY),
    readQueryParam("country"),
    env.VITE_ORVIS_COUNTRY,
  ];

  for (const c of candidates) {
    if (!c) continue;
    const normalized = c.toLowerCase();
    if (getCountry(normalized)) {
      // If the choice came from the URL, persist it so a plain refresh
      // stays on that country instead of falling through to env.
      if (readLocalStorage(COUNTRY_STORAGE_KEY) === undefined) {
        writeLocalStorage(COUNTRY_STORAGE_KEY, normalized);
      }
      return normalized;
    }
  }

  // No valid hint anywhere.
  if (canSwitch) return undefined;
  return DEFAULT_FALLBACK_COUNTRY;
}

/**
 * Full runtime config — call once at boot. Throws only when the resolved
 * country code refers to a country the config package doesn't know.
 * Callers must handle `undefined` (picker case) BEFORE calling this.
 */
export function loadRuntimeConfig(code: string, env: ImportMetaEnv = import.meta.env): RuntimeConfig {
  const country = getCountry(code);
  if (!country) {
    throw new Error(
      `Unknown country "${code}". Known: ${COUNTRY_ORDER.join(", ")}. Add a file to packages/config/src/countries/ to register more.`
    );
  }
  return {
    country,
    firebase: readFirebaseConfig(env),
    canSwitch: env.VITE_ORVIS_ALLOW_SWITCH === "true",
  };
}

/**
 * Called by the picker (or the "Change country" button after a
 * confirmation). Persists the choice and triggers a full reload so
 * everything downstream — Firestore reads, i18n, queries — reboots
 * clean.
 */
export function commitCountryChoice(code: string): void {
  if (!getCountry(code.toLowerCase())) return;
  writeLocalStorage(COUNTRY_STORAGE_KEY, code.toLowerCase());
  if (typeof location !== "undefined") {
    // Drop the query param — localStorage holds truth now.
    location.assign(location.pathname);
  }
}

export function clearCountryChoice(): void {
  try {
    if (typeof localStorage !== "undefined") localStorage.removeItem(COUNTRY_STORAGE_KEY);
  } catch {
    /* ignore */
  }
  if (typeof location !== "undefined") location.assign(location.pathname);
}
